import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  DEFAULT_TIME_LIMIT_MS,
  initialSelection,
  limitOptions,
  pickLocalized,
  quizLanguages,
  type BinogQuestion,
  type LanguageMode,
  type NameMode,
  type QuestionLevel,
} from "@binogi-livequiz/shared";
import Wordmark from "../components/Wordmark";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { api } from "../lib/api";
import {
  isPlaceholderLessonCode,
  isValidLessonCode,
  LESSON_CODE_MAX_LENGTH,
  lessonRequestFromParams,
  LessonLoadError,
  loadLesson,
  PLACEHOLDER_TITLE,
  type LoadedLesson,
} from "../lib/lesson";
import { saveHostToken } from "../lib/session-storage";
import { ButtonGroup } from "../components/ui/ButtonGroup";
import { StudentCountInput } from "../components/ui/StudentCountInput";
import { TabButton } from "../components/ui/TabButton";

type Phase = "input" | "loading" | "ready" | "starting";

// An error line as an i18n key and its values, translated at render.
interface ErrorText {
  key: string;
  values?: Record<string, string>;
}

/** The copy for a failed lesson or question load. */
function loadErrorText(err: unknown): ErrorText {
  return err instanceof LessonLoadError
    ? { key: err.i18nKey, values: { code: err.lessonCode } }
    : { key: "host.error_load_failed" };
}

const TIME_OPTIONS = [10_000, 15_000, 20_000, 30_000] as const;
const SUGGESTED_CODES = ["BIH750", "GEH101", "PHY1137"] as const;

export default function HostReview() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();

  const [phase, setPhase] = useState<Phase>("input");
  const [lessonCode, setLessonCode] = useState("BIH750");
  const [loaded, setLoaded] = useState<LoadedLesson | null>(null);
  const [curated, setCurated] = useState<BinogQuestion[]>([]);
  const [nameMode, setNameMode] = useState<NameMode>("character");
  const [languageMode, setLanguageMode] = useState<LanguageMode>("studentChoice");
  const [timeLimitMs, setTimeLimitMs] =
    useState<number>(DEFAULT_TIME_LIMIT_MS);
  // Default is 0 — teacher has to actively pick a class size before starting
  // (required field on the Details tab). Everything else has a usable default.
  const [expectedStudents, setExpectedStudents] = useState<number>(0);
  // Teacher's custom title override. Empty string means "use the
  // lesson's localized title from the API as-is". Treated as opt-in:
  // we show the lesson title as the input placeholder.
  const [customTitle, setCustomTitle] = useState<string>("");
  const [error, setError] = useState<ErrorText | null>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const autoHandledRef = useRef(false);

  const adoptLesson = useCallback((lesson: LoadedLesson) => {
    // Cap every question at four options, once per load: the drop is
    // random, and this capped set is what gets sent, so the server's own
    // cap is a no-op and stores exactly these options. Then preselect —
    // the five shortest for a lesson code, everything for a staged or
    // inline set. The teacher can add the rest from the list below.
    const questions = lesson.questions.map((q) => limitOptions(q));
    setLoaded({ ...lesson, questions });
    setCurated(initialSelection(questions, lesson.source));
    setLessonCode(lesson.lessonCode);
    setCustomTitle("");
    setPhase("ready");
  }, []);

  useEffect(() => {
    if (autoHandledRef.current) return;
    const request = lessonRequestFromParams(searchParams);
    if (!request) return;
    autoHandledRef.current = true;

    (async () => {
      setPhase("loading");
      setError(null);
      try {
        adoptLesson(await loadLesson(request));
      } catch (err) {
        setError(loadErrorText(err));
        setPhase("input");
      }
    })();
  }, [adoptLesson, searchParams]);

  const handleLoad = useCallback(async () => {
    const code = lessonCode.trim().toUpperCase();
    if (!isValidLessonCode(code)) {
      setError({ key: "host.input.error_invalid_code" });
      return;
    }
    setPhase("loading");
    setError(null);
    try {
      adoptLesson(await loadLesson({ source: "lesson", code, levels: [] }));
    } catch (err) {
      setError(loadErrorText(err));
      setPhase("input");
    }
  }, [adoptLesson, lessonCode]);

  const handleStart = useCallback(async () => {
    if (!loaded || curated.length === 0) {
      setError({ key: "host.error_no_questions" });
      return;
    }
    setPhase("starting");
    setError(null);
    // Teacher's override wins; otherwise fall back to whatever the
    // lesson API gave us in the current preview language.
    const finalTitle =
      customTitle.trim() || pickLocalized(loaded.title, i18n.language);
    try {
      const res = await api.createSession({
        questions: curated,
        nameMode,
        languageMode,
        timeLimitMs,
        expectedStudents,
        lessonCode: loaded.lessonCode,
        lessonTitle: finalTitle,
      });
      // Persist the one-time host token to sessionStorage so a
      // refresh of the lobby/question screens (same tab) can still
      // re-attach over WebSocket. sessionStorage dies with the tab —
      // a second teacher device cannot pick the token back up.
      saveHostToken(res.sessionCode, res.hostToken);
      // Fallback: mid-deploy the old create-session Lambda omits `questions`.
      const sessionQuestions = res.questions ?? curated;
      navigate("/host/lobby", {
        state: {
          sessionCode: res.sessionCode,
          joinUrl: res.joinUrl,
          hostToken: res.hostToken,
          // The server's stored copy (options trimmed + ordered), not
          // `curated` — HostQuestion seeds round 1 from this before
          // QUESTION_START arrives, and must show the students' order.
          questions: sessionQuestions,
          timeLimitMs,
          nameMode,
          expectedStudents,
          languageMode,
          // Compute on the questions the server stored (the curated set,
          // options trimmed) so a level filter or manual removal decides
          // which langs the picker offers, and the value matches what
          // http-create-session stored via quizLanguages().
          availableLanguages: quizLanguages(
            sessionQuestions.flatMap((q) => [
              q.text,
              ...q.options.map((o) => o.text),
            ]),
          ),
          lessonTitle: finalTitle,
        },
      });
    } catch {
      setError({ key: "host.error_create_failed" });
      setPhase("ready");
    }
  }, [
    curated,
    customTitle,
    expectedStudents,
    i18n.language,
    languageMode,
    loaded,
    nameMode,
    navigate,
    timeLimitMs,
  ]);

  const handleReorder = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setCurated((prev) => {
      const oldIdx = prev.findIndex((q) => q.qid === active.id);
      const newIdx = prev.findIndex((q) => q.qid === over.id);
      if (oldIdx < 0 || newIdx < 0) return prev;
      return arrayMove(prev, oldIdx, newIdx);
    });
  }, []);

  const handleRemove = useCallback((qid: string) => {
    setCurated((prev) => prev.filter((q) => q.qid !== qid));
  }, []);

  const handleAdd = useCallback(
    (qid: string) => {
      const q = loaded?.questions.find((x) => x.qid === qid);
      if (!q) return;
      setCurated((prev) =>
        prev.some((x) => x.qid === qid) ? prev : [...prev, q],
      );
    },
    [loaded],
  );

  const handleAddAll = useCallback(() => {
    if (!loaded) return;
    setCurated((prev) => {
      const have = new Set(prev.map((q) => q.qid));
      return [...prev, ...loaded.questions.filter((q) => !have.has(q.qid))];
    });
  }, [loaded]);

  return (
    <main className="min-h-full px-4 md:px-6 py-6 md:py-10 max-w-2xl mx-auto pb-32 relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <header className="flex justify-center mb-10">
        <Wordmark size="lg" />
      </header>

      {phase === "input" && (
        <InputPhase
          lessonCode={lessonCode}
          setLessonCode={setLessonCode}
          onLoad={handleLoad}
          codeInputRef={codeInputRef}
          error={error}
        />
      )}

      {phase === "loading" && (
        <div className="text-center text-white/70 py-16">
          <div className="inline-flex items-center gap-3">
            <span className="w-3 h-3 rounded-full bg-brand-green animate-pulseSoft" />
            <span>{t("host.loading")}</span>
          </div>
        </div>
      )}

      {(phase === "ready" || phase === "starting") && loaded && (
        <ReadyPhase
          loaded={loaded}
          curated={curated}
          nameMode={nameMode}
          setNameMode={setNameMode}
          languageMode={languageMode}
          setLanguageMode={setLanguageMode}
          multiLanguage={(loaded?.languages.length ?? 0) > 1}
          timeLimitMs={timeLimitMs}
          setTimeLimitMs={setTimeLimitMs}
          expectedStudents={expectedStudents}
          setExpectedStudents={setExpectedStudents}
          customTitle={customTitle}
          setCustomTitle={setCustomTitle}
          onStart={handleStart}
          onReorder={handleReorder}
          onRemove={handleRemove}
          onAdd={handleAdd}
          onAddAll={handleAddAll}
          starting={phase === "starting"}
          error={error}
        />
      )}
    </main>
  );
}

interface InputPhaseProps {
  lessonCode: string;
  setLessonCode: (s: string) => void;
  onLoad: () => void;
  codeInputRef: React.RefObject<HTMLInputElement>;
  error: ErrorText | null;
}

function InputPhase({
  lessonCode,
  setLessonCode,
  onLoad,
  codeInputRef,
  error,
}: InputPhaseProps) {
  const { t } = useTranslation();
  return (
    <section className="bg-brand-ink2 rounded-3xl p-7 md:p-10">
      <h1 className="text-2xl md:text-3xl font-bold mb-2">
        {t("host.input.heading")}
      </h1>
      <p className="text-white/70 mb-6">{t("host.input.lead")}</p>

      <label className="block text-sm text-white/70 mb-2" htmlFor="lessonCode">
        {t("host.input.lesson_code_label")}
      </label>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          id="lessonCode"
          ref={codeInputRef}
          type="text"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          value={lessonCode}
          onChange={(e) =>
            setLessonCode(
              e.target.value
                .toUpperCase()
                .replace(/[^A-Z0-9_-]/g, "")
                .slice(0, LESSON_CODE_MAX_LENGTH),
            )
          }
          onKeyDown={(e) => {
            if (e.key === "Enter") onLoad();
          }}
          placeholder={t("host.input.placeholder_code")}
          className="flex-1 bg-brand-ink text-2xl font-bold tracking-wider px-5 py-3 rounded-xl outline-none focus:ring-2 focus:ring-brand-green placeholder:text-white/20"
        />
        <button
          type="button"
          onClick={onLoad}
          className="bg-brand-green text-white font-bold px-6 py-3 rounded-xl hover:bg-brand-greenHi"
        >
          {t("host.input.load_button")}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-white/50">
        <span>{t("host.input.try_hint")}</span>
        {SUGGESTED_CODES.map((c) => (
          <button
            key={c}
            type="button"
            onClick={() => setLessonCode(c)}
            className="px-2 py-1 rounded-full bg-brand-ink3 hover:bg-brand-ink3/80 font-mono text-white/80"
          >
            {c}
          </button>
        ))}
      </div>

      {error && (
        <p className="mt-5 text-sm text-brand-bad">
          {t(error.key, error.values)}
        </p>
      )}
    </section>
  );
}

interface ReadyPhaseProps {
  loaded: LoadedLesson;
  curated: BinogQuestion[];
  nameMode: NameMode;
  setNameMode: (m: NameMode) => void;
  languageMode: LanguageMode;
  setLanguageMode: (m: LanguageMode) => void;
  // True when the curated questions carry translations in 2+ languages.
  // Single-language sources (inline questions, staged payloads, lessons
  // that only published one locale) hide the Language toggle entirely
  // because it can't change anything meaningful.
  multiLanguage: boolean;
  timeLimitMs: number;
  setTimeLimitMs: (n: number) => void;
  expectedStudents: number;
  setExpectedStudents: (n: number) => void;
  customTitle: string;
  setCustomTitle: (s: string) => void;
  onStart: () => void;
  onReorder: (e: DragEndEvent) => void;
  onRemove: (qid: string) => void;
  onAdd: (qid: string) => void;
  onAddAll: () => void;
  starting: boolean;
  error: ErrorText | null;
}

type Tab = "details" | "questions";

function ReadyPhase({
  loaded,
  curated,
  nameMode,
  setNameMode,
  languageMode,
  setLanguageMode,
  multiLanguage,
  timeLimitMs,
  setTimeLimitMs,
  expectedStudents,
  setExpectedStudents,
  customTitle,
  setCustomTitle,
  onStart,
  onReorder,
  onRemove,
  onAdd,
  onAddAll,
  starting,
  error,
}: ReadyPhaseProps) {
  const { t, i18n } = useTranslation();
  // Resolve the lesson title in the teacher's current preview language.
  // Inline/staged sources arrive with the literal string
  // "Questions from Binogi" — swap that for a localized placeholder so
  // the input prompt makes sense in any locale.
  const localizedTitle = pickLocalized(loaded.title, i18n.language);
  const titlePlaceholder =
    localizedTitle === PLACEHOLDER_TITLE ||
    isPlaceholderLessonCode(loaded.lessonCode)
      ? t("host.placeholder_title")
      : localizedTitle;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );
  const curatedIds = new Set(curated.map((q) => q.qid));
  const available = loaded.questions.filter((q) => !curatedIds.has(q.qid));
  const [tab, setTab] = useState<Tab>("details");
  const canStart =
    !starting && curated.length > 0 && expectedStudents > 0;

  return (
    <section className="space-y-5">
      <div className="text-center">
        <input
          type="text"
          value={customTitle}
          onChange={(e) => setCustomTitle(e.target.value)}
          placeholder={titlePlaceholder}
          aria-label={titlePlaceholder}
          className="w-full max-w-md mx-auto block text-center text-2xl md:text-3xl font-bold rounded-lg px-3 py-1.5 bg-brand-ink2/40 ring-1 ring-white/10 outline-none placeholder:text-white hover:bg-brand-ink2/60 hover:ring-white/20 focus:bg-brand-ink2/70 focus:ring-2 focus:ring-brand-green/50 transition-colors"
        />
        <button
          type="button"
          onClick={() => setTab("questions")}
          className="mt-2 text-sm text-white/60 hover:text-white focus:outline-none focus-visible:underline"
        >
          {t("common.questions_count", { count: curated.length })}
        </button>
      </div>

      {/* Tabs — Binogi-style underline */}
      <div className="flex justify-center gap-10 border-b border-white/10">
        <TabButton
          active={tab === "details"}
          onClick={() => setTab("details")}
        >
          {t("host.tab_details")}
          {expectedStudents === 0 && (
            <span className="ml-1.5 inline-block w-1.5 h-1.5 rounded-full bg-brand-amber align-middle" />
          )}
        </TabButton>
        <TabButton
          active={tab === "questions"}
          onClick={() => setTab("questions")}
        >
          {t("host.tab_questions")}
        </TabButton>
      </div>

      {tab === "details" ? (
        <div className="space-y-8 max-w-xl mx-auto">
          <StudentCountInput
            value={expectedStudents}
            onChange={setExpectedStudents}
          />

          <div className="bg-brand-ink2/50 ring-1 ring-white/5 rounded-2xl px-5 py-5 space-y-4">
            <h3 className="text-xs uppercase tracking-wide text-white/40 font-semibold">
              {t("host.settings")}
            </h3>
            <ButtonGroup
              label={t("host.player_names")}
              value={nameMode}
              onChange={(v) => setNameMode(v as NameMode)}
              options={[
                { value: "character", label: t("host.player_anonymous") },
                { value: "free", label: t("host.player_self_chosen") },
              ]}
            />
            {multiLanguage && (
              <ButtonGroup
                label={t("host.language")}
                value={languageMode}
                onChange={(v) => setLanguageMode(v as LanguageMode)}
                options={[
                  { value: "lockedEn", label: t("host.language_teacher") },
                  { value: "studentChoice", label: t("host.language_students") },
                ]}
              />
            )}
            <ButtonGroup
              label={t("host.time_per_question")}
              value={timeLimitMs}
              onChange={setTimeLimitMs}
              options={TIME_OPTIONS.map((ms) => ({
                value: ms,
                label: t("host.time_option", { seconds: ms / 1000 }),
              }))}
            />
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {curated.length === 0 ? (
            <div className="rounded-2xl border-2 border-dashed border-white/10 p-10 text-center">
              <p className="text-white/70 mb-2">
                {t("host.questions.empty")}
              </p>
              <button
                type="button"
                onClick={onAddAll}
                className="text-brand-green font-semibold"
              >
                {t("host.questions.add_all_n", { count: loaded.questions.length })}
              </button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={onReorder}
            >
              <SortableContext
                items={curated.map((q) => q.qid)}
                strategy={verticalListSortingStrategy}
              >
                <ul className="space-y-2">
                  {curated.map((q, i) => (
                    <li key={q.qid}>
                      <QuestionRow
                        question={q}
                        index={i}
                        onRemove={() => onRemove(q.qid)}
                      />
                    </li>
                  ))}
                </ul>
              </SortableContext>
            </DndContext>
          )}
          {available.length > 0 && (
            <div className="pt-4 space-y-2">
              <div className="flex items-center justify-between text-xs text-white/50">
                <span>
                  {t("host.questions.more", { count: available.length })}
                </span>
                {/* With nothing selected the empty state above already
                    offers "Add all N questions". */}
                {curated.length > 0 && (
                  <button
                    type="button"
                    onClick={onAddAll}
                    className="text-brand-green hover:text-white font-semibold"
                  >
                    {t("host.questions.add_all")}
                  </button>
                )}
              </div>
              <ul className="space-y-2">
                {available.map((q) => (
                  <li key={q.qid}>
                    <AvailableQuestionRow question={q} onAdd={() => onAdd(q.qid)} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Floating Start CTA — sticks to the bottom of the viewport */}
      <div className="fixed bottom-5 inset-x-4 md:left-1/2 md:right-auto md:-translate-x-1/2 z-30 pointer-events-none">
        <div className="max-w-md mx-auto pointer-events-auto">
          <button
            type="button"
            onClick={
              canStart
                ? onStart
                : () => {
                    if (expectedStudents === 0) setTab("details");
                  }
            }
            disabled={starting}
            className={`w-full font-bold py-5 px-8 rounded-full text-lg leading-tight shadow-2xl transition-all ${
              canStart
                ? "bg-brand-green hover:bg-brand-greenHi text-white shadow-brand-green/30"
                : "bg-brand-ink2 text-white/50 cursor-not-allowed shadow-black/30"
            } ${starting ? "opacity-60" : ""}`}
          >
            {starting
              ? t("host.creating")
              : expectedStudents === 0
                ? t("host.start_disabled")
                : t("host.start_button")}
          </button>
          {error && (
            <p className="mt-2 text-xs text-brand-bad text-center bg-brand-ink/80 backdrop-blur rounded-full px-3 py-1">
              {t(error.key, error.values)}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}

interface AvailableQuestionRowProps {
  question: BinogQuestion;
  onAdd: () => void;
}

function AvailableQuestionRow({ question, onAdd }: AvailableQuestionRowProps) {
  const { t, i18n } = useTranslation();
  const level = LEVEL_META[question.level];
  const previewText = pickLocalized(question.text, i18n.language);
  return (
    <div className="bg-brand-ink2/40 rounded-xl p-3 flex items-center gap-3 ring-1 ring-white/5">
      <span
        className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${level.classes} flex-shrink-0`}
      >
        {t(level.i18nKey)}
      </span>
      <span className="text-sm text-white/60 flex-1 min-w-0 truncate" title={previewText}>
        {previewText}
      </span>
      <button
        type="button"
        onClick={onAdd}
        className="text-brand-green hover:text-white text-sm font-semibold flex-shrink-0"
        // Enough of the question to tell rows apart without a screen
        // reader reading a whole long question on every tab stop.
        aria-label={t("host.questions.add_aria", {
          text:
            previewText.length > 80
              ? `${previewText.slice(0, 80)}…`
              : previewText,
        })}
      >
        + {t("host.questions.add")}
      </button>
    </div>
  );
}

interface QuestionRowProps {
  question: BinogQuestion;
  index: number;
  onRemove: () => void;
}

const LEVEL_META: Record<
  QuestionLevel,
  { i18nKey: string; classes: string }
> = {
  1: { i18nKey: "host.questions.level_easy", classes: "bg-brand-good/15 text-brand-good" },
  2: { i18nKey: "host.questions.level_medium", classes: "bg-brand-amber/20 text-brand-amber" },
  3: { i18nKey: "host.questions.level_hard", classes: "bg-brand-bad/15 text-brand-bad" },
};

function QuestionRow({ question, index, onRemove }: QuestionRowProps) {
  const { t, i18n } = useTranslation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.qid });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 30 : "auto",
  };
  const level = LEVEL_META[question.level];
  const previewText = pickLocalized(question.text, i18n.language);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-brand-ink2 rounded-xl p-3 flex items-center gap-3 hover:bg-brand-ink2/80"
    >
      <button
        {...attributes}
        {...listeners}
        type="button"
        className="cursor-grab active:cursor-grabbing text-white/40 hover:text-white/70 px-1"
        aria-label={t("host.questions.drag_aria")}
      >
        <svg width="14" height="20" viewBox="0 0 14 20" aria-hidden="true">
          <g fill="currentColor">
            <circle cx="4" cy="5" r="1.5" />
            <circle cx="4" cy="10" r="1.5" />
            <circle cx="4" cy="15" r="1.5" />
            <circle cx="10" cy="5" r="1.5" />
            <circle cx="10" cy="10" r="1.5" />
            <circle cx="10" cy="15" r="1.5" />
          </g>
        </svg>
      </button>
      <span className="text-xs font-semibold text-white/60 tabular-nums w-6 flex-shrink-0">
        {index + 1}
      </span>
      <span
        className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${level.classes} flex-shrink-0`}
      >
        {t(level.i18nKey)}
      </span>
      {question.type === "multiChoice" && (
        <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-brand-purple/25 text-brand-purple flex-shrink-0">
          {t("host.questions.multi")}
        </span>
      )}
      <span className="text-sm flex-1 min-w-0 truncate" title={previewText}>
        {previewText}
      </span>
      <button
        type="button"
        onClick={onRemove}
        className="text-white/40 hover:text-brand-bad text-xl leading-none w-6 flex-shrink-0"
        aria-label={t("host.questions.remove_aria")}
      >
        ×
      </button>
    </div>
  );
}
